// Shared by the browser and the existing extraction endpoint.
export type DatePrecision = 'day' | 'month' | 'season' | 'year' | 'range' | 'unknown'
export type Season = 'spring' | 'summer' | 'fall' | 'winter'
export type SemanticDate = {
  precision: DatePrecision
  date?: string | null
  endDate?: string | null
  year?: number | null
  month?: number | null
  season?: Season | null
}
export type EventDate = {
  date: Date | string | null
  endDate?: Date | string | null
  datePrecision?: DatePrecision | null
}

// New calendar dates are encoded as UTC midnight in the existing timestamp columns.
// Legacy timestamps retain their local-time semantics.
export function parseCalendarDate(value: string | null | undefined): Date | null {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null
  const date = new Date(`${value}T00:00:00.000Z`)
  return Number.isFinite(date.getTime()) && date.toISOString().slice(0, 10) === value ? date : null
}

export function resolveYearOnly(narrative: string): SemanticDate | null {
  const match = /^\s*(?:sometime\s+)?in\s+(\d{4})\b/i.exec(narrative)
  if (!match || (narrative.match(/\b\d{4}\b/g) ?? []).length !== 1) return null
  // Only handle a plain year opening; more specific or competing timing stays
  // with the existing extraction path.
  if (/\b(jan(?:uary)?|feb(?:ruary)?|march|apr(?:il)?|may|june?|july?|aug(?:ust)?|sept?(?:ember)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?|spring|summer|fall|autumn|winter|today|yesterday|tomorrow|last|next|before|after|or)\b|\b\d{1,2}[/-]\d{1,2}\b/i.test(narrative.slice(match[0].length))) return null
  const year = Number(match[1])
  return year >= 1 && year <= 9998 ? { precision: 'year', year } : null
}

export function resolveMonthAlternatives(narrative: string): SemanticDate | null {
  const names = ['january', 'february', 'march', 'april', 'may', 'june', 'july', 'august', 'september', 'october', 'november', 'december']
  const months = names.map(name => name === 'may' ? name : `${name}|${name.slice(0, 3)}\\.?`).join('|')
  const matches = [...narrative.matchAll(new RegExp(`\\b(${months})(?:\\s+(\\d{4}))?\\s+or\\s+(${months})\\s+(\\d{4})\\b`, 'gi'))]
  if (matches.length !== 1 || (narrative.match(/\b\d{4}\b/g) ?? []).length !== (matches[0][2] ? 2 : 1)) return null
  const match = matches[0]
  const remainder = narrative.slice(0, match.index!) + narrative.slice(match.index! + match[0].length)
  if (new RegExp(`\\b(${months}|today|yesterday|tomorrow)\\b|\\b\\d{1,2}[/-]\\d{1,2}\\b`, 'i').test(remainder)) return null
  const alternatives = [
    { month: names.findIndex(name => name.startsWith(match[1].toLowerCase().replace('.', ''))) + 1, year: Number(match[2] ?? match[4]) },
    { month: names.findIndex(name => name.startsWith(match[3].toLowerCase().replace('.', ''))) + 1, year: Number(match[4]) },
  ].sort((a, b) => a.year * 12 + a.month - (b.year * 12 + b.month))
  const [first, last] = alternatives
  // A small, explicitly bounded case: two adjacent named months with years.
  if (last.year * 12 + last.month !== first.year * 12 + first.month + 1) return null
  const start = resolveEventDate({ precision: 'month', ...first })
  const end = resolveEventDate({ precision: 'month', ...last })
  if (!start.date || !end.endDate) return null
  return { precision: 'range', date: calendarDate(start.date), endDate: calendarDate(end.endDate) }
}

// A small set of explicit opening phrases; referenceDate is the user's calendar day.
export function resolveRelativeDay(narrative: string, referenceDate: string): SemanticDate | null {
  const match = /^\s*(today|yesterday|tomorrow|last\s+(?:week\s+on\s+)?(sunday|monday|tuesday|wednesday|thursday|friday|saturday))\b/i.exec(narrative)
  if (!match) return null
  const remainder = narrative.slice(match[0].length)
  // Leave competing date clues to extraction rather than guessing which event they date.
  if (/\b\d{4}\b|\b(today|yesterday|tomorrow|sunday|monday|tuesday|wednesday|thursday|friday|saturday)\b/i.test(remainder)) return null
  const date = parseCalendarDate(referenceDate)
  if (!date) return null
  let offset: number
  if (match[2]) {
    const target = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'].indexOf(match[2].toLowerCase())
    const distance = (date.getUTCDay() - target + 7) % 7
    offset = -(distance || 7)
  } else {
    offset = ({ today: 0, yesterday: -1, tomorrow: 1 })[match[1].toLowerCase() as 'today' | 'yesterday' | 'tomorrow']
  }
  date.setUTCDate(date.getUTCDate() + offset)
  const result = calendarDate(date)
  return parseCalendarDate(result) ? { precision: 'day', date: result } : null
}

export function calendarDate(value: Date | string | null | undefined, legacy = false): string {
  if (!value) return ''
  const date = new Date(value)
  if (!Number.isFinite(date.getTime())) return ''
  if (!legacy) return date.toISOString().slice(0, 10)
  return `${String(date.getFullYear()).padStart(4, '0')}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
}

export function resolveEventDate(input: SemanticDate | null | undefined): {
  date: Date | null
  endDate: Date | null
  datePrecision: DatePrecision
} {
  const unknown = { date: null, endDate: null, datePrecision: 'unknown' as const }
  if (!input || input.precision === 'unknown') return unknown
  if (input.precision === 'range') {
    const date = parseCalendarDate(input.date)
    const endDate = parseCalendarDate(input.endDate)
    return date && endDate && endDate >= date
      ? { date, endDate, datePrecision: 'range' }
      : unknown
  }
  if (input.precision === 'day') {
    const date = parseCalendarDate(input.date)
    return date ? { date, endDate: new Date(date), datePrecision: 'day' } : unknown
  }
  if (!Number.isInteger(input.year) || input.year! < 1 || input.year! > 9998) return unknown
  const year = String(input.year).padStart(4, '0')
  let start: string
  let end: string
  const monthEnd = (y: number, m: number) => {
    const next = parseCalendarDate(
      `${String(m === 12 ? y + 1 : y).padStart(4, '0')}-${String(m === 12 ? 1 : m + 1).padStart(2, '0')}-01`,
    )!
    next.setUTCDate(0)
    return next.toISOString().slice(0, 10)
  }
  if (input.precision === 'year') {
    start = `${year}-01-01`
    end = `${year}-12-31`
  } else if (input.precision === 'month') {
    if (!Number.isInteger(input.month) || input.month! < 1 || input.month! > 12) return unknown
    start = `${year}-${String(input.month).padStart(2, '0')}-01`
    end = monthEnd(input.year!, input.month!)
  } else if (input.precision === 'season') {
    const starts = { spring: 3, summer: 6, fall: 9, winter: 12 }
    const m = input.season ? starts[input.season] : undefined
    if (!m) return unknown
    start = `${year}-${String(m).padStart(2, '0')}-01`
    end = input.season === 'winter' ? monthEnd(input.year! + 1, 2) : monthEnd(input.year!, m + 2)
  } else return unknown
  return {
    date: parseCalendarDate(start),
    endDate: parseCalendarDate(end),
    datePrecision: input.precision,
  }
}

const months = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC']
const monthNames = [
  'JANUARY',
  'FEBRUARY',
  'MARCH',
  'APRIL',
  'MAY',
  'JUNE',
  'JULY',
  'AUGUST',
  'SEPTEMBER',
  'OCTOBER',
  'NOVEMBER',
  'DECEMBER',
]
export function formatEventDate(event: EventDate): string {
  const start = calendarDate(event.date, !event.datePrecision)
  if (event.datePrecision === 'unknown' || !start) return 'Date unknown'
  const [year, month, day] = start.split('-').map(Number)
  if (event.datePrecision === 'range') {
    const end = calendarDate(event.endDate)
    if (!end || end < start) return 'Date unknown'
    const endYear = Number(end.slice(0, 4))
    const endMonth = Number(end.slice(5, 7))
    const endDay = Number(end.slice(8, 10))
    const monthEnd = resolveEventDate({ precision: 'month', year: endYear, month: endMonth }).endDate
    if (day === 1 && endYear * 12 + endMonth === year * 12 + month + 1 && monthEnd?.getUTCDate() === endDay) {
      return `${months[month - 1]} ${year}–${months[endMonth - 1]} ${endYear}`
    }
    return endYear === year ? `${year} (approx.)` : `${year}–${endYear}`
  }
  if (event.datePrecision === 'year') return String(year)
  if (event.datePrecision === 'month') return `${monthNames[month - 1]} ${year}`
  if (event.datePrecision === 'season') {
    const season = (
      { 3: 'SPRING', 6: 'SUMMER', 9: 'FALL', 12: 'WINTER' } as Record<number, string>
    )[month]
    return season ? `${season} ${year}` : 'Date unknown'
  }
  const label = `${months[month - 1]} ${day}, ${year}`
  const end = calendarDate(event.endDate, true)
  if (!event.datePrecision && end && end !== start) {
    return `${label} – ${formatEventDate({ date: event.endDate! })}`
  }
  return label
}

export function isPlaced(event: EventDate): boolean {
  return event.datePrecision !== 'unknown' && Boolean(calendarDate(event.date))
}

export function compareEventDates(
  a: EventDate & { id: number },
  b: EventDate & { id: number },
): number {
  // Descending chronology; unplaced events never borrow a chronological timestamp.
  if (!isPlaced(a) || !isPlaced(b)) return Number(isPlaced(b)) - Number(isPlaced(a)) || b.id - a.id
  return new Date(b.date!).getTime() - new Date(a.date!).getTime() || b.id - a.id
}

export function overlapsDateFilter(
  event: EventDate,
  begin: string | null,
  end: string | null,
): boolean {
  if (!begin && !end) return true
  if (!isPlaced(event)) return false
  const start = calendarDate(event.date, !event.datePrecision)
  const finish = calendarDate(event.endDate, !event.datePrecision) || start
  return (!begin || finish >= begin) && (!end || start <= end)
}
